import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import { queryClient } from '@/data/reactQueryClient';
import { useProcessCardRemoval } from '@/feature/account-details/hooks/use-process-card-removal';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useNoPaymentMethodDeposit } from '@/feature/withdraw/hooks/use-no-payment-method-deposit';
import { selectAllWithdrawMethods, useWithdrawalMethods } from '@/feature/withdraw/hooks/use-withdrawal-methods';
import { filterWithdrawableMethods, getWithdrawHomeRoute } from '@/feature/withdraw/utils/utils';
import { useFantasyMoneyFlags } from '@/hooks/payments/use-fantasy-money-flags';
import { useDebitCardRemovalStore } from '@/hooks/use-debit-card-removal-store';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { trustlyInfoKeys } from '@/hooks/use-user-trustly-information';
import { KycStackNavigationProp } from '@/navigation/kyc/types';
import { kycProceedToFinancialStack, popToHome, resetToAccountScreen } from '@/navigation/navigation';
import { IdpvSource, IdpvSourceType } from '@/navigation/types';

import { kycRouteNames } from '../const';

export const useNavigateToSuccessModal = ({ source = IdpvSource.KYC }: { source?: IdpvSourceType }) => {
    const navigation = useNavigation<KycStackNavigationProp>();
    const rootNavigation = useRootNavigation();
    const { t } = useTranslation(['manage_cards', 'common']);
    const tSocialWalletAccount = useSocialWalletTranslation('account');
    const tSocialWallet = useSocialWalletTranslation('wallet');
    const {
        description: noPaymentMethodSubtitle,
        depositLabel: makeDepositLabel,
        depositRoute,
    } = useNoPaymentMethodDeposit();

    const { paymentTypeId } = useDebitCardRemovalStore();
    const { handleVerificationSuccess } = useProcessCardRemoval();
    const handleVerificationSuccessRef = useRef(handleVerificationSuccess);
    useEffect(() => {
        handleVerificationSuccessRef.current = handleVerificationSuccess;
    });
    const { isFantasyMoneyRedemptionEnabled } = useFantasyMoneyFlags();
    // Disabled observer: only refetched on demand in the WITHDRAW branch, so mounting this hook doesn't trigger a refetch of the withdrawal methods query.
    const { refetch: refetchWithdrawalMethods } = useWithdrawalMethods({
        enabled: false,
        select: selectAllWithdrawMethods,
    });

    const withdrawSuccessTitle = tSocialWalletAccount('withdraw_verification_success_title');
    const withdrawSuccessSubtitle = tSocialWalletAccount('withdraw_verification_success_description');
    const proceedToWithdrawalLabel = tSocialWallet('proceed_to_withdrawal');

    const navigateToSuccessModal = useCallback(async () => {
        switch (source) {
            case IdpvSource.ACCOUNT_SETTING:
                // home isn't remounted, so refetch what the IDPV changed (withdraw gate)
                queryClient.refetchQueries({ queryKey: trustlyInfoKeys.all });
                navigation.navigate('SuccessModal', {
                    title: t('enhance_profile_complete'),
                    subtitle: t('profile_enhanced_confirmation'),
                    primaryButton: t('common:done'),
                    closeIconMode: 'close',
                    handlePress: () => popToHome(rootNavigation, 'lobby'),
                    closeIconHandlePress: () => popToHome(rootNavigation, 'account'),
                });
                return;
            case IdpvSource.MANAGE_CARD:
                navigation.navigate('SuccessModal', {
                    title: t('enhance_profile_complete'),
                    subtitle: t('profile_enhanced_confirmation'),
                    primaryButton: t('continue_to_card_removal'),
                    closeIconMode: 'close',
                    handlePress: () => handleVerificationSuccessRef.current(paymentTypeId),
                    closeIconHandlePress: () => resetToAccountScreen(rootNavigation, 'ManageDebitCards'),
                });
                return;
            case IdpvSource.WITHDRAW: {
                // The IDPV outcome lifts the verification gate and the closed-loop
                queryClient.refetchQueries({ queryKey: trustlyInfoKeys.all });
                const methodsResult = await refetchWithdrawalMethods();
                const freshWithdrawMethods = methodsResult.data ?? [];
                const validWithdrawMethods = filterWithdrawableMethods(freshWithdrawMethods, {
                    // This branch only runs after a successful IDPV, so the verification gate is lifted.
                    isVerifiedWithRestrictedBalance: true,
                    needsVerificationByLightSdd: false,
                });
                // Verified but nothing on file to withdraw to — send the user to save a
                // payment method via deposit instead of into the withdraw flow. A failed
                // refetch means the list is unknown, not empty, so it falls through to the
                // success modal instead (an empty list routes to the chooser, which
                // fetches its own data).
                if (!methodsResult.isError && validWithdrawMethods.length === 0) {
                    BetrAnalytics.trackProductEvent(AnalyticsEvent.WITHDRAW_VERIFICATION_NO_PAYMENT_METHOD);
                    navigation.navigate('SuccessModal', {
                        title: withdrawSuccessTitle,
                        subtitle: noPaymentMethodSubtitle,
                        primaryButton: makeDepositLabel,
                        closeIconMode: 'close',
                        handlePress: () =>
                            kycProceedToFinancialStack(navigation, { name: 'DepositStack', params: depositRoute }),
                        closeIconHandlePress: () => popToHome(rootNavigation, 'account'),
                    });
                    return;
                }
                navigation.navigate('SuccessModal', {
                    title: withdrawSuccessTitle,
                    subtitle: withdrawSuccessSubtitle,
                    primaryButton: proceedToWithdrawalLabel,
                    closeIconMode: 'close',
                    handlePress: () =>
                        kycProceedToFinancialStack(navigation, {
                            name: 'WithdrawStack',
                            params: getWithdrawHomeRoute(validWithdrawMethods, isFantasyMoneyRedemptionEnabled),
                        }),
                    closeIconHandlePress: () => popToHome(rootNavigation, 'account'),
                });
                return;
            }
            default: // IdpvSource.KYC
                // Reset the KYC stack to prevent the user from going back to the previous screens as the KYC process is complete
                navigation.reset({
                    index: 0,
                    routes: [{ name: kycRouteNames.VERIFICATION_SUCCESS }],
                });
                return;
        }
    }, [
        navigation,
        rootNavigation,
        source,
        paymentTypeId,
        t,
        withdrawSuccessTitle,
        withdrawSuccessSubtitle,
        proceedToWithdrawalLabel,
        noPaymentMethodSubtitle,
        makeDepositLabel,
        depositRoute,
        refetchWithdrawalMethods,
        isFantasyMoneyRedemptionEnabled,
    ]);

    return navigateToSuccessModal;
};
